import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Percent } from "@phosphor-icons/react";
import { TAX_DISPLAY_MODES } from "../builderStorage";

/**
 * Section 4 — Tax Display Option.
 *
 * Pure presentation choice for the PDF — no GST math is applied to the
 * scope or commercial total. This is a visual flag the preview consumes.
 */
export default function TaxDisplaySection({ taxDisplay, onChange }) {
    return (
        <Card className="border-slate-200" data-testid="section-tax-display">
            <CardHeader>
                <CardTitle className="flex items-center gap-2"><Percent weight="duotone" /> Section 3 — Tax Display Option</CardTitle>
                <CardDescription>
                    Choose how taxes are presented on the proposal. This affects only the PDF wording — not the underlying commercials.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <RadioGroup
                    value={taxDisplay.mode}
                    onValueChange={(v) => onChange({ ...taxDisplay, mode: v })}
                    className="grid md:grid-cols-2 gap-2"
                    data-testid="tax-mode-group"
                >
                    {TAX_DISPLAY_MODES.map((m) => (
                        <Label
                            key={m.code}
                            htmlFor={`tax-${m.code}`}
                            className={`flex items-start gap-2 p-3 rounded-md border cursor-pointer transition ${
                                taxDisplay.mode === m.code
                                    ? "bg-indigo-50 border-indigo-300"
                                    : "border-slate-200 hover:bg-slate-50"
                            }`}
                        >
                            <RadioGroupItem value={m.code} id={`tax-${m.code}`} data-testid={`tax-mode-${m.code}`} />
                            <span className="text-sm">{m.label}</span>
                        </Label>
                    ))}
                </RadioGroup>

                {taxDisplay.mode === "show_gst" && (
                    <div className="grid gap-3 md:grid-cols-[160px_1fr]">
                        <div>
                            <Label className="text-xs uppercase tracking-wide text-slate-500">GST percent</Label>
                            <Input
                                type="number"
                                value={taxDisplay.gstPercent ?? 18}
                                onChange={(e) => onChange({
                                    ...taxDisplay,
                                    gstPercent: e.target.value === "" ? 0 : Number(e.target.value),
                                })}
                                placeholder="18"
                                className="mt-2 text-right"
                                data-testid="tax-gst-percent"
                            />
                        </div>
                        <div className="text-xs text-slate-500 italic flex items-end">
                            Common rates: 0, 5, 12, 18, 28. The PDF will compute and show CGST/SGST or IGST in a separate row.
                        </div>
                    </div>
                )}

                {taxDisplay.mode === "taxes_extra" && (
                    <div className="text-xs text-slate-500 italic">
                        Proposal will display &quot;<strong>Taxes extra as applicable</strong>&quot; — no GST breakdown rendered.
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
